// src/utils/deliveryUtils.ts
// Utilities to estimate delivery date ranges.

const isBusinessDay = (d: Date): boolean => {
  const day = d.getDay(); // 0 Sunday, 6 Saturday
  return day !== 0 && day !== 6;
};

const addBusinessDays = (date: Date, days: number): Date => {
  const out = new Date(date);
  let added = 0;
  while (added < days) {
    out.setDate(out.getDate() + 1);
    if (isBusinessDay(out)) added++;
  }
  return out;
};

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

export interface DeliveryConfig {
  enabled: boolean;
  useSellerAddress: boolean;
  defaultProcessingDays: number;
  defaultTransitLocal: number;
  defaultTransitRegional: number;
  defaultTransitInterstate: number;
  cutoffHour: number;
  cutoffApplies?: boolean;
  businessDaysOnly: boolean;
  cityOverrides?: Record<string, number>;
  transitBufferDays?: number;
}

export interface Seller {
  city?: string;
  state?: string;
  processingTimeDays?: number;
  processingTimeDaysMin?: number;
  processingTimeDaysMax?: number;
}

export interface BuyerAddress {
  city?: string;
  state?: string;
}

export interface DeliveryEstimate {
  earliest: Date;
  latest: Date;
  minDays: number;
  maxDays: number;
  label: string;
}

/**
 * Estimate delivery for a single item given seller + buyer + config.
 * Returns { earliest: Date, latest: Date, label: string (e.g. "1-3 business days") } or null if cannot estimate
 */
export function estimateDeliveryForItem({
  seller,
  buyerAddress,
  now = new Date(),
  config
}: {
  seller: Seller;
  buyerAddress: BuyerAddress | null;
  now?: Date;
  config: DeliveryConfig;
}): DeliveryEstimate | null {
  if (!config || !config.enabled) return null;
  
  // get seller location fields
  const sellerCity = (seller?.city || "").trim();
  const sellerState = (seller?.state || "").trim();
  const buyerCity = (buyerAddress?.city || "").trim();
  const buyerState = (buyerAddress?.state || "").trim();

  // processing time (seller-specific takes precedence)
  let proc = seller?.processingTimeDays ?? config.defaultProcessingDays ?? 1;
  // clamp sensible range
  proc = clamp(Number(proc || 0), 0, 30);

  // if cutoff applies and now is after cutoff -> processing starts next day
  let startDate = new Date(now);
  const cutoff = config.cutoffHour ?? 15;
  if (config.cutoffApplies && (now.getHours() >= cutoff)) {
    startDate.setDate(startDate.getDate() + 1);
  }

  // if businessDaysOnly, make sure startDate is a business day
  if (config.businessDaysOnly) {
    while (!isBusinessDay(startDate)) startDate.setDate(startDate.getDate() + 1);
  }

  // transit days: local / regional / interstate
  let transitBase: number;
  // check city override
  if (config.cityOverrides && sellerCity && config.cityOverrides[sellerCity]) {
    transitBase = Number(config.cityOverrides[sellerCity]);
  } else if (sellerState && buyerState && sellerState.toLowerCase() === buyerState.toLowerCase()) {
    // same state -> local/regional decision: same city = local else regional
    transitBase = (sellerCity && buyerCity && sellerCity.toLowerCase() === buyerCity.toLowerCase())
      ? (config.defaultTransitLocal ?? 1)
      : (config.defaultTransitRegional ?? 2);
  } else {
    // interstate
    transitBase = config.defaultTransitInterstate ?? 3;
  }
  transitBase = clamp(Number(transitBase || 0), 0, 30);

  // total days windows: we use seller.processingTimeDaysMin/Max if present
  let procMin = seller?.processingTimeDaysMin ?? proc;
  let procMax = seller?.processingTimeDaysMax ?? proc;
  procMin = clamp(Number(procMin || 0), 0, 30);
  procMax = clamp(Number(procMax || 0), procMin, 60);

  // transit min can be same as transitBase; optionally use seller overrides if present
  let transitMin = transitBase;
  let transitMax = transitBase + (config.transitBufferDays ?? 1); // small buffer

  // Compute earliest and latest arrival
  let earliest = new Date(startDate);
  // apply procMin
  if (config.businessDaysOnly) earliest = addBusinessDays(earliest, procMin);
  else earliest.setDate(earliest.getDate() + procMin);

  if (config.businessDaysOnly) earliest = addBusinessDays(earliest, transitMin);
  else earliest.setDate(earliest.getDate() + transitMin);

  let latest = new Date(startDate);
  if (config.businessDaysOnly) latest = addBusinessDays(latest, procMax);
  else latest.setDate(latest.getDate() + procMax);

  if (config.businessDaysOnly) latest = addBusinessDays(latest, transitMax);
  else latest.setDate(latest.getDate() + transitMax);

  // create friendly label - show "business days" if businessDaysOnly true
  const minDays = procMin + transitMin;
  const maxDays = procMax + transitMax;
  const dayUnit = config.businessDaysOnly ? "business day" : "day";
  const plural = (minDays === maxDays) ? `${minDays} ${dayUnit}${minDays > 1 ? 's' : ''}` : `${minDays}–${maxDays} ${dayUnit}${maxDays > 1 ? 's' : ''}`;

  return {
    earliest,
    latest,
    minDays,
    maxDays,
    label: plural,
  };
}

/**
 * Combine multiple item estimates into a single overall estimate.
 * Strategy: earliest overall = min(item.earliest), latest overall = max(item.latest)
 */
export function combineEstimates(estimates: (DeliveryEstimate | null)[]): DeliveryEstimate | null {
  const valid = (estimates || []).filter(Boolean) as DeliveryEstimate[];
  if (!valid.length) return null;
  
  let earliest = valid[0].earliest;
  let latest = valid[0].latest;
  let minDays = valid[0].minDays;
  let maxDays = valid[0].maxDays;
  
  for (let i = 1; i < valid.length; i++) {
    if (valid[i].earliest < earliest) earliest = valid[i].earliest;
    if (valid[i].latest > latest) latest = valid[i].latest;
    minDays = Math.min(minDays, valid[i].minDays);
    maxDays = Math.max(maxDays, valid[i].maxDays);
  }
  
  return {
    earliest,
    latest,
    minDays,
    maxDays,
    label: minDays === maxDays ? `${minDays} ${minDays > 1 ? 'days' : 'day'}` : `${minDays}-${maxDays} days`
  };
}

/** format date like "16 Dec 2025" */
export function formatDate(d: Date | null): string {
  if (!d) return '';
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
  return new Intl.DateTimeFormat('en-IN', opts).format(d);
}